import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useHttp } from 'src/hooks';
import { AuthContext } from './auth-context';

export interface CreativeMetrics {
    leads: string;
    clicks: string;
    costPerLead: string;
    costPerClick: string;
    hookRate: string;
    holdRate: string;
    impressions: string;
    spend: string;
    purchases: string;
    purchaseValue: string;
}

export interface TimeSeriesInsight {
    date: string;
    impressions: number;
    clicks: number;
    spend: number;
    leads: number;
    costPerLead: number;
}

export interface Campaign {
    id: string;
    name: string;
    status: string;
}

export interface Audience {
    id: string;
    name: string;
    type: string;
}

export interface Creative {
    id: string;
    name: string;
    thumbnail: string;
    isVideo: boolean;
    title: string;
    description: string;
    adAccountId: string;
    adAccountName: string;
    metrics: CreativeMetrics;
    creativeType: string;
    createdAt: string;
    // Additional fields for modal
    timeSeriesInsights: TimeSeriesInsight[];
    campaigns: Campaign[];
    audiences: Audience[];
    targeting: any;
}

// Update the Creative interface in your context to include the new fields
export interface Creative {
    id: string;
    name: string;
    thumbnail: string;
    isVideo: boolean;
    title: string;
    description: string;
    adAccountId: string;
    adAccountName: string;
    metrics: CreativeMetrics;
    creativeType: string;
    createdAt: string;
    timeSeriesInsights: TimeSeriesInsight[];
    campaigns: Campaign[];
    audiences: Audience[];
    targeting: any;
}

interface CreativeContextType {
    creatives: Creative[];
    isLoading: boolean;
    error: string | null;
    fetchCreatives: (seeAll?: boolean) => Promise<void>;
    totalCount: number;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    itemsPerPage: number;
    seeAll: boolean;
    setSeeAll: (seeAll: boolean) => void;
}

const CreativeContext = createContext<CreativeContextType | undefined>(undefined);

interface CreativeProviderProps {
    children: ReactNode;
}

export const CreativeProvider: React.FC<CreativeProviderProps> = ({ children }) => {
    const [creatives, setCreatives] = useState<Creative[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(12);
    const [seeAll, setSeeAll] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    const { sendRequest, isLoading } = useHttp();
    const { accessToken, profile } = useContext(AuthContext);

    const fetchCreatives = async (seeAllParam?: boolean) => {
        if (!accessToken) {
            setError('Authentication required');
            return;
        }

        const shouldSeeAll = seeAllParam !== undefined ? seeAllParam : seeAll;

        setError(null);

        try {
            await sendRequest(
                {
                    url: `ad-creative/?seeAll=${shouldSeeAll}`,
                    method: 'GET',
                    accessToken,
                },
                (data: Creative[]) => {
                    if (data && Array.isArray(data)) {
                        setCreatives(data);
                        setTotalCount(data.length);
                        setSeeAll(shouldSeeAll);
                    } else {
                        setError('Invalid data format received from server');
                    }
                }
            );
        } catch (err: any) {
            setError(err.message || 'An unexpected error occurred');
        }
    };

    const value: CreativeContextType = {
        creatives,
        isLoading,
        error,
        fetchCreatives,
        totalCount,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        seeAll,
        setSeeAll,
    };

    useEffect(() => {
        if (accessToken) {
            fetchCreatives();
        }
    }, [accessToken, profile]);

    return (
        <CreativeContext.Provider value={value}>
            {children}
        </CreativeContext.Provider>
    );
};

export const useCreative = (): CreativeContextType => {
    const context = useContext(CreativeContext);
    if (context === undefined) {
        throw new Error('useCreative must be used within a CreativeProvider');
    }
    return context;
};